import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Button, Table, Select, Space, Typography,
  Empty, Spin, message, Input, Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, UnorderedListOutlined, SearchOutlined } from '@ant-design/icons';
import iconPlot from '../../icons/plot.png';
import { useNavigate } from 'react-router-dom';
import { getPlotsApi } from '../../api/crops';
import { AREA_UNITS } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';
import dayjs from 'dayjs';
import AddPlotModal from './AddPlotModal';
import PlotMiniMap from './PlotMiniMap';

const { Title, Text } = Typography;

export default function Plots() {
  const navigate = useNavigate();
  const [plots, setPlots] = useState([]);
  const [farmFilter, setFarmFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlot, setEditingPlot] = useState(null);

  const farmOptions = useMemo(() => {
    const seen = new Set();
    return plots
      .filter((p) => p.farm_name && !seen.has(p.farm_id) && seen.add(p.farm_id))
      .map((p) => ({ value: p.farm_id, label: p.farm_name }));
  }, [plots]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (farmFilter) params.farm_id = farmFilter;
      if (debouncedSearch.trim()) params.name = debouncedSearch.trim();
      const res = await getPlotsApi(params);
      setPlots(res.data?.data || []);
    } catch {
      message.error('Failed to load plots');
    } finally {
      setLoading(false);
    }
  }, [farmFilter, debouncedSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    {
      title: 'Plot',
      key: 'plot',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, textTransform: 'capitalize' }}>📍 {record.name || '—'}</div>
          <PlotMiniMap path={record.path} activeCrop={!!record.latest_crop_name} />
        </div>
      ),
    },
    {
      title: 'Active Crop',
      key: 'latest_crop',
      render: (_, record) => {
        if (!record.latest_crop_name) return <Text type="secondary" style={{ fontSize: 13 }}>—</Text>;
        const days = record.latest_crop_date ? dayjs().diff(dayjs(record.latest_crop_date), 'day') : null;
        return (
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#1a6b2a', textTransform: 'capitalize' }}>{record.latest_crop_name}</div>
            {record.latest_crop_date && (
              <div style={{ fontSize: 13, color: '#595959', marginTop: 3 }}>{formatDate(record.latest_crop_date)}</div>
            )}
            {days !== null && (
              <div style={{ fontSize: 12, color: '#1a6b2a', marginTop: 2, fontWeight: 500 }}>crop age: {days} days</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Farm',
      key: 'farm',
      render: (_, record) => <Text>{record.farm_name || '—'}</Text>,
    },
    {
      title: 'Area',
      key: 'area',
      render: (_, r) =>
        r.seted_area
          ? `${r.seted_area} ${AREA_UNITS[r.seted_area_unit] || r.seted_area_unit}`
          : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => { e.stopPropagation(); setEditingPlot(record); setModalOpen(true); }}
          />
          <Button
            size="small"
            icon={<UnorderedListOutlined />}
            onClick={(e) => { e.stopPropagation(); navigate(`/plots/${record.id}`); }}
            style={{ borderColor: '#1a6b2a', color: '#1a6b2a' }}
          >
            All Crops
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={4} style={{ margin: 0, color: '#1a6b2a', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, backgroundColor: '#1a6b2a', WebkitMask: `url(${iconPlot}) no-repeat center / contain`, mask: `url(${iconPlot}) no-repeat center / contain`, flexShrink: 0 }} />
          Farm Plots
        </Title>
        <Space wrap>
          <Input
            placeholder="Search by name"
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Select
            allowClear
            placeholder="All Farms"
            value={farmFilter}
            onChange={setFarmFilter}
            options={farmOptions}
            style={{ minWidth: 180 }}
            showSearch
            filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
          >
            Add Plot
          </Button>
        </Space>
      </div>

      <Card style={{ borderRadius: 10 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : plots.length === 0 ? (
          <Empty description="No plots yet — add your first plot with a geofenced boundary">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalOpen(true)}
              style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
            >
              Add Plot
            </Button>
          </Empty>
        ) : (
          <Table
            dataSource={plots}
            columns={columns}
            rowKey="id"
            scroll={{ x: 700 }}
            onRow={(record) => ({
              onClick: () => navigate(`/plots/${record.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Card>

      <AddPlotModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingPlot(null); }}
        onSuccess={() => { setModalOpen(false); setEditingPlot(null); fetchData(); }}
        initialPlot={editingPlot}
      />
    </div>
  );
}
